import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Trash2, ShoppingCart, Tag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';

export const CartDrawer: React.FC = () => {
  const {
    items,
    isOpen,
    toggleCart,
    removeFromCart,
    updateQuantity,
    subtotal,
    tax,
    discount,
    total,
    promoCode,
    applyPromoCode,
    removePromoCode,
  } = useCart();

  const [promoInput, setPromoInput] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;

    setApplyingPromo(true);
    const success = await applyPromoCode(promoInput);
    if (success) {
      setPromoInput('');
    }
    setApplyingPromo(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleCart}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white border-l border-stone-200 z-50 flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between p-6 border-b border-stone-200">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-6 h-6 text-royal-gold" />
                <h2 className="text-2xl font-bold text-royal-text">Shopping Cart</h2>
              </div>
              <button
                onClick={toggleCart}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-stone-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-24 h-24 bg-stone-100 rounded-full flex items-center justify-center mb-4">
                    <ShoppingCart className="w-12 h-12 text-stone-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-royal-text mb-2">Your cart is empty</h3>
                  <p className="text-royal-text-secondary mb-6">
                    Add some items to get started
                  </p>
                  <button
                    onClick={toggleCart}
                    className="px-6 py-3 bg-royal-gold text-white font-semibold rounded-xl hover:bg-royal-gold-dark transition-colors"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="flex gap-4 p-4 bg-stone-50 rounded-xl border border-stone-200"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-20 h-20 rounded-xl object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-royal-text mb-1">{item.name}</h3>
                        <p className="text-sm text-royal-text-secondary capitalize mb-2">{item.type}</p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center hover:bg-stone-200 transition-colors"
                          >
                            <Minus className="w-4 h-4 text-stone-500" />
                          </button>
                          <span className="w-8 text-center text-royal-text font-medium">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center hover:bg-stone-200 transition-colors"
                          >
                            <Plus className="w-4 h-4 text-stone-500" />
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                        >
                          <Trash2 className="w-4 h-4 text-stone-400 group-hover:text-red-500" />
                        </button>
                        <p className="text-lg font-bold text-royal-gold">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-stone-200 p-6 space-y-4 bg-stone-50">
                <div className="space-y-2">
                  <label className="text-sm text-royal-text-secondary flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Promo Code
                  </label>
                  {promoCode ? (
                    <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-700">
                          {promoCode.code}
                        </span>
                      </div>
                      <button
                        onClick={removePromoCode}
                        className="text-xs text-red-500 hover:text-red-600 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="flex-1 px-4 py-2 bg-white border border-stone-200 rounded-xl text-royal-text placeholder:text-stone-400 focus:outline-none focus:border-royal-gold/50 focus:ring-1 focus:ring-royal-gold/20"
                      />
                      <button
                        onClick={handleApplyPromo}
                        disabled={applyingPromo || !promoInput.trim()}
                        className="px-4 py-2 bg-royal-gold text-white font-semibold rounded-xl hover:bg-royal-gold-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {applyingPromo ? 'Applying...' : 'Apply'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2 pt-4 border-t border-stone-200">
                  <div className="flex justify-between text-royal-text-secondary">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Discount</span>
                      <span>-${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-royal-text-secondary">
                    <span>Estimated Tax (8%)</span>
                    <span>${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold text-royal-text pt-2 border-t border-stone-200">
                    <span>Total</span>
                    <span className="text-royal-gold">${total.toFixed(2)}</span>
                  </div>
                </div>

                <Link
                  to="/checkout"
                  onClick={toggleCart}
                  className="block w-full py-4 bg-royal-gold text-white text-center font-bold rounded-xl hover:bg-royal-gold-dark transition-colors shadow-lg shadow-royal-gold/20"
                >
                  Proceed to Checkout
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
